import { useState } from 'react';
import { Alert, Avatar, Box, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/Edit';
import { useUploadImagem } from './use-upload-imagem';
import { CapaLoja } from '../capa-loja';

/**
 * A caixa da prévia tem fundo branco fixo (imita o app), então as cores do texto
 * NÃO podem sair da paleta: no tema escuro `text.primary` é branco e o nome da
 * loja ficava branco no branco — invisível.
 */
const COR_TEXTO = '#1a1a1a';
const COR_TEXTO_FRACO = 'rgba(0,0,0,0.5)';

interface Props {
  urlCover: string | null;
  urlLogo: string | null;
  nome: string;
  onChangeCover: (url: string | null) => void;
  onChangeLogo: (url: string | null) => void;
  /** Quando informado, o nome vira editável no próprio lugar (ícone de lápis). */
  onChangeNome?: (nome: string) => void;
}

/**
 * Prévia EDITÁVEL do topo do cardápio: capa, logo e nome aparecem como no app e
 * se editam no próprio lugar — o controle fica sobre o elemento, sem campos
 * separados. Fundo branco, igual ao cardápio.
 */
export function PreviewVisualCardapio({
  urlCover,
  urlLogo,
  nome,
  onChangeCover,
  onChangeLogo,
  onChangeNome,
}: Props): JSX.Element {
  const capa = useUploadImagem('capa', onChangeCover);
  const logo = useUploadImagem('logo', onChangeLogo);
  const [editandoNome, setEditandoNome] = useState(false);

  return (
    <Box>
      <Box sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
        {/* Capa (banner) — a própria imagem é o botão de trocar */}
        <Box
          onClick={capa.abrirSeletor}
          sx={{
            position: 'relative',
            height: 120,
            borderRadius: 2,
            mb: 1.5,
            overflow: 'hidden',
            cursor: 'pointer',
            bgcolor: 'grey.100',
            border: urlCover ? 'none' : '1px dashed',
            borderColor: 'divider',
            '&:hover .overlay-capa': { opacity: 1 },
          }}
        >
          {/* Mesmo componente do cardápio: o lojista vê a capa como o cliente vai ver */}
          {urlCover && <CapaLoja url={urlCover} sx={{ position: 'absolute', inset: 0, height: '100%' }} />}
          <Box
            className="overlay-capa"
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              pointerEvents: 'none',
              color: urlCover ? 'common.white' : 'text.secondary',
              bgcolor: urlCover ? 'rgba(0,0,0,0.35)' : 'transparent',
              // sem capa ou enviando: sempre visível; com capa: só no hover
              opacity: !urlCover || capa.enviando ? 1 : 0,
              transition: 'opacity .2s',
            }}
          >
            {capa.enviando ? (
              <CircularProgress size={22} />
            ) : (
              <>
                <PhotoCameraIcon fontSize="small" />
                <Typography variant="caption">{urlCover ? 'Trocar capa' : 'Adicionar capa'}</Typography>
              </>
            )}
          </Box>

          {urlCover && !capa.enviando && (
            <IconButton
              size="small"
              aria-label="Remover capa"
              onClick={(e) => {
                e.stopPropagation();
                onChangeCover(null);
              }}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.4)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}

          {capa.inputArquivo}
        </Box>

        {/* Logo + nome (logo também é clicável para trocar) */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            onClick={logo.abrirSeletor}
            sx={{
              position: 'relative',
              width: 56,
              height: 56,
              flexShrink: 0,
              cursor: 'pointer',
              '&:hover .overlay-logo': { opacity: 1 },
            }}
          >
            <Avatar
              src={urlLogo ?? undefined}
              variant="rounded"
              sx={{
                width: 56,
                height: 56,
                // logo transparente não ganha fundo cinza (igual ao app)
                bgcolor: urlLogo ? 'transparent' : 'grey.100',
                color: 'text.secondary',
              }}
            >
              {!urlLogo && <PhotoCameraIcon fontSize="small" />}
            </Avatar>

            <Box
              className="overlay-logo"
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                color: 'common.white',
                bgcolor: 'rgba(0,0,0,0.45)',
                opacity: logo.enviando ? 1 : 0,
                transition: 'opacity .2s',
              }}
            >
              {logo.enviando ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <PhotoCameraIcon sx={{ fontSize: 18 }} />
              )}
            </Box>

            {urlLogo && !logo.enviando && (
              <IconButton
                size="small"
                aria-label="Remover logo"
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeLogo(null);
                }}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  p: 0.25,
                  bgcolor: 'error.main',
                  color: '#fff',
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}

            {logo.inputArquivo}
          </Box>

          {editandoNome && onChangeNome ? (
            <TextField
              autoFocus
              fullWidth
              variant="standard"
              value={nome}
              placeholder="Nome da loja"
              inputProps={{ 'aria-label': 'Nome da loja', maxLength: 200 }}
              onChange={(e) => onChangeNome(e.target.value)}
              // Confirma no Enter, no Esc e ao sair do campo. Não há "cancelar":
              // o valor já foi para o formulário a cada tecla, e desfazer aqui
              // divergiria do resto da tela, que só desfaz não salvando.
              onBlur={() => setEditandoNome(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') setEditandoNome(false);
              }}
              sx={{
                minWidth: 0,
                '& .MuiInputBase-input': { color: COR_TEXTO, fontSize: '1.25rem', fontWeight: 500, py: 0 },
                '& .MuiInput-underline:before': { borderBottomColor: 'rgba(0,0,0,0.25)' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(0,0,0,0.45)' },
              }}
            />
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap sx={{ minWidth: 0, color: COR_TEXTO }}>
                {nome || 'Nome da loja'}
              </Typography>
              {onChangeNome && (
                <IconButton
                  size="small"
                  aria-label="Editar nome da loja"
                  onClick={() => setEditandoNome(true)}
                  sx={{ color: COR_TEXTO_FRACO, flexShrink: 0, '&:hover': { color: COR_TEXTO } }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Stack>
          )}
        </Stack>

        {(capa.erro || logo.erro) && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {capa.erro || logo.erro}
          </Alert>
        )}
      </Box>
    </Box>
  );
}
